import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Stack,
    Typography,
} from '@mui/material';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import { campaignDataAnalysisFilterSchema } from 'common/schema';
import React, { useEffect } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDatePicker from 'common/components/base-date-picker';
import RequiredLabel from 'common/components/required-label';
import { comparisonOperators } from 'pages/admin/campaign-overview/constants';
import BaseTextField from 'common/components/base-text-field';
import { colors } from 'common/constants';
import { handleError } from 'utils/helpers';
import {
    filterQuestionData,
    getListQuestionFilter,
} from 'services/campaign.service';
import { useParams } from 'react-router-dom';
import { IListQuestionFilterResponse } from 'types/campaign';
import { useAppDispatch } from 'common/hook';
import { setListQuestionDataFilter } from 'slices/questionSummarySlice';
import { setIsLoading } from 'slices/loadingSlice';
import dayjs from 'dayjs';
import { setFilter } from 'slices/dataAnalyticsFilterSlice';

const AnswersFetcher: React.FC<{
    questionId: number;
    index: number;
    listQuestionsFilter: IListQuestionFilterResponse[];
}> = ({ questionId, index, listQuestionsFilter }) => {
    const { setValue } = useFormContext();
    useEffect(() => {
        if (!questionId) return;
        setValue(`questions.${index}.answers`, []);
        setValue(`questions.${index}.date`, undefined);
        setValue(`questions.${index}.number`, '');
        setValue(`questions.${index}.operator`, '');
        setValue(`questions.${index}.availableAnswers`, []);
        const fetchAnswers = async () => {
            const question = listQuestionsFilter.find(
                (item) => item.attributes.id === questionId
            );
            setValue(
                `questions.${index}.availableAnswers`,
                question?.attributes.answer_options.map((item) => {
                    return {
                        label: item.text,
                        value: item.id,
                    };
                }),
                {
                    shouldValidate: true,
                }
            );
            setValue(
                `questions.${index}.questionType`,
                question?.attributes.kind
            );
            setValue(`questions.${index}.questionId`, questionId);
        };
        fetchAnswers();
    }, [questionId, index, setValue, listQuestionsFilter]);
    return null;
};
const Filter: React.FC = () => {
    const dispatch = useAppDispatch();
    const campaignId = useParams<{ id: string }>().id;
    const [listQuestionsFilter, setListQuestionsFilter] =
        React.useState<IListQuestionFilterResponse[]>();
    const methods = useForm({
        resolver: yupResolver(campaignDataAnalysisFilterSchema),
        defaultValues: {
            questions: [
                {
                    questionType: '',
                    questionId: '',
                    answers: [],
                    operator: '',
                    date: undefined,
                    number: '',
                },
            ],
        },
        mode: 'onSubmit',
    });
    useEffect(() => {
        const fetchListQuestions = async () => {
            try {
                const response = await getListQuestionFilter(
                    Number(campaignId),
                    'filter'
                );
                if (response.success) {
                    setListQuestionsFilter(response.data);
                }
            } catch (error) {
                handleError(error);
            }
        };
        fetchListQuestions();
    }, [campaignId]);
    const { watch, handleSubmit } = methods;
    const { fields, append, remove } = useFieldArray({
        control: methods.control,
        name: 'questions',
    });

    const onSubmit = handleSubmit(async (data) => {
        // eslint-disable-next-line
        const payloads = (data?.questions ?? []).map((item) => {
            const { questionId, operator, date } = item;
            if (
                [
                    'checkbox',
                    'single_choice',
                    'dropdown',
                    'textbox_recommend',
                    'textbox_recommend_multi_choice',
                ].includes(item.questionType ?? '')
            ) {
                return {
                    question_id: questionId,
                    answer_option_ids: item.answers,
                };
            }
            if (['date'].includes(item.questionType ?? '')) {
                return {
                    question_id: questionId,
                    condition: operator,
                    value: dayjs(date).format('DD/MM/YYYY'),
                };
            }
            if (
                [
                    'number',
                    'slider_scale',
                    'score_star_full',
                    'score_star_half',
                    'score',
                    'emoji',
                ].includes(item.questionType ?? '')
            ) {
                return {
                    question_id: questionId,
                    condition: operator,
                    value: Number(item.number),
                };
            }
            if (['rank'].includes(item.questionType ?? '')) {
                return {
                    question_id: questionId,
                    answer_option_ids: item.answers?.reduce(
                        (acc, curr, index) => {
                            acc[curr.value] = index + 1;
                            return acc;
                        },
                        {}
                    ),
                };
            }
        });
        try {
            dispatch(setIsLoading(true));
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dispatch(setFilter(payloads as any));
            const response = await filterQuestionData(payloads);
            if (response.success) {
                dispatch(setListQuestionDataFilter(response.data));
                dispatch(setIsLoading(false));
            }
        } catch (error) {
            handleError(error);
        } finally {
            dispatch(setIsLoading(false));
        }
    });
    return (
        <Box>
            <Typography
                sx={{
                    textAlign: 'left',
                    fontSize: '18px',
                    fontWeight: '600',
                    mb: 1,
                    mt: 2,
                }}
            >
                Lựa chọn câu hỏi & câu trả lời
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                {fields.map((field, index) => {
                    const questionId = watch(`questions.${index}.questionId`);
                    const questionType = watch(
                        `questions.${index}.questionType`
                    );

                    return (
                        <Box
                            key={field.id}
                            sx={{
                                borderBottom: '1px solid rgba(0, 0, 0, 0.6)',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: 'center',
                                    mb: 2,
                                    mt: 2,
                                }}
                            >
                                <BaseSelectField
                                    size="small"
                                    name={`questions.${index}.questionId`}
                                    sx={{
                                        textAlign: 'left',
                                    }}
                                >
                                    {listQuestionsFilter?.map(
                                        (question, key) => (
                                            <MenuItem
                                                key={key}
                                                value={question.attributes.id}
                                            >
                                                {question.attributes.title}
                                            </MenuItem>
                                        )
                                    )}
                                </BaseSelectField>

                                <IconButton
                                    // disabled={index === 0}
                                    onClick={() => remove(index)}
                                    sx={{
                                        display:
                                            fields.length === 1
                                                ? 'none'
                                                : 'inline-flex',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                        '&:hover': {
                                            backgroundColor:
                                                'rgba(255, 0, 0, 0.2)',
                                        },
                                    }}
                                >
                                    <CloseIcon
                                        sx={{
                                            width: '16px',
                                            height: '16px',
                                            color: (theme) =>
                                                theme.palette.error.main,
                                        }}
                                    />
                                </IconButton>
                            </Box>

                            {questionId && (
                                <AnswersFetcher
                                    questionId={Number(questionId)}
                                    index={index}
                                    listQuestionsFilter={
                                        listQuestionsFilter ?? []
                                    }
                                />
                            )}
                            {[
                                'checkbox',
                                'single_choice',
                                'dropdown',

                                'textbox_recommend',
                                'textbox_recommend_multi_choice',
                            ].includes(questionType ?? '') &&
                                questionId && (
                                    <Box mb={2}>
                                        <BaseMultiCheckbox
                                            key={index}
                                            name={`questions.${index}.answers`}
                                            sxCheckbox={{
                                                '& .MuiFormControlLabel-label':
                                                    {
                                                        fontWeight: '500',
                                                    },
                                                '&.Mui-checked + .MuiFormControlLabel-label':
                                                    {
                                                        color: colors.main,
                                                        fontWeight: '500',
                                                    },
                                            }}
                                            options={
                                                watch(
                                                    `questions.${index}.availableAnswers`
                                                ) as {
                                                    value: string;
                                                    label: string;
                                                }[]
                                            }
                                        />
                                    </Box>
                                )}
                            {['rank'].includes(questionType ?? '') &&
                                questionId && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 2,
                                            mb: 2,
                                        }}
                                    >
                                        {watch(
                                            `questions.${index}.availableAnswers`
                                        )?.map((item, key) => (
                                            <Box
                                                key={key}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                <Typography sx={{ flex: 0.2 }}>
                                                    Vị trí {key + 1}
                                                </Typography>
                                                <BaseSelectField
                                                    sx={{
                                                        flex: 0.5,
                                                        width: '50%',
                                                    }}
                                                    size="small"
                                                    name={`questions.${index}.answersRank[${key}].value`}
                                                >
                                                    {watch(
                                                        `questions.${index}.availableAnswers`
                                                    )?.map((answer, ansKey) => (
                                                        <MenuItem
                                                            key={ansKey}
                                                            value={answer.value}
                                                        >
                                                            {answer.label}
                                                        </MenuItem>
                                                    ))}
                                                </BaseSelectField>
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            {['date'].includes(questionType ?? '') &&
                                questionId && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: 2,
                                            mb: 2,
                                        }}
                                    >
                                        <Stack flex={0.5}>
                                            <RequiredLabel
                                                label="So sánh"
                                                required={false}
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                            <BaseSelectField
                                                name={`questions.${index}.operator`}
                                                size="small"
                                            >
                                                {comparisonOperators.map(
                                                    (item, key) => (
                                                        <MenuItem
                                                            key={key}
                                                            value={item.value}
                                                        >
                                                            {item.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </BaseSelectField>
                                        </Stack>
                                        <Stack flex={1}>
                                            <RequiredLabel
                                                label="Ngày"
                                                required={false}
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                            <BaseDatePicker
                                                size="small"
                                                name={`questions.${index}.date`}
                                            />
                                        </Stack>
                                    </Box>
                                )}
                            {[
                                'number',
                                'slider_scale',
                                'score_star_full',
                                'score_star_half',
                                'score',
                                'emoji',
                            ].includes(questionType ?? '') &&
                                questionId && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            justifyContent: 'space-between',
                                            gap: 2,
                                            mb: 2,
                                        }}
                                    >
                                        <Stack flex={0.5}>
                                            <RequiredLabel
                                                label="So sánh"
                                                required={false}
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                            <BaseSelectField
                                                name={`questions.${index}.operator`}
                                                size="small"
                                            >
                                                {comparisonOperators.map(
                                                    (item, key) => (
                                                        <MenuItem
                                                            key={key}
                                                            value={item.value}
                                                        >
                                                            {item.label}
                                                        </MenuItem>
                                                    )
                                                )}
                                            </BaseSelectField>
                                        </Stack>
                                        <Stack flex={1}>
                                            <RequiredLabel
                                                label="Giá trị"
                                                required={false}
                                                sx={{
                                                    fontSize: '16px',
                                                }}
                                            />
                                            <BaseTextField
                                                placeholder="Vui lòng nhập số"
                                                size="small"
                                                name={`questions.${index}.number`}
                                                fullWidth
                                                autoComplete="off"
                                                InputProps={{
                                                    inputProps: {
                                                        maxLength: 22,
                                                    },
                                                }}
                                            />
                                        </Stack>
                                    </Box>
                                )}
                        </Box>
                    );
                })}
                <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
                    <Button
                        onClick={() =>
                            append({
                                questionType: '',
                                questionId: '',
                                answers: undefined,
                                operator: '',
                                date: undefined,
                                number: '',
                            })
                        }
                        variant="outlined"
                        sx={{
                            maxWidth: '84px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            borderRadius: '40px',
                        }}
                    >
                        Thêm
                    </Button>

                    <Button
                        sx={{
                            maxWidth: '84px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '14px',
                            borderRadius: '40px',
                        }}
                        type="submit"
                        variant="contained"
                    >
                        Lọc
                    </Button>
                </Box>
            </FormProvider>
        </Box>
    );
};

export default Filter;
